import { useEffect, useMemo, useState } from 'react'
import { DocumentPlusIcon } from '@heroicons/react/24/solid'
import { useHistory } from '../../../providers/HistoryProvider'
import { Modal } from '../../common/Modal'
import { TemplateObject } from '../../../utils/TemplateObject'
import { NewNoteInitialSelection } from './modalpages/NewNoteInitialSelection'
import { SelectInputsForNotes } from './modalpages/SelectInputsForNotes'
import { UploadRecordingMainPage } from '../../recording/modalpages/UploadRecordingMainPage'
import { RecordingUploadMode } from '../../../utils/UploadUtils'

enum NewNotePage {
    INITIAL_SELECTION = "INITIAL_SELECTION",
    UPLOAD_RECORDING = "UPLOAD_RECORDING",
    SELECT_RECORDINGS = "SELECT_RECORDINGS",
}

export default function NewNoteModal() {
    const {
        showAddNoteModal, 
        handleShowAddNotesModalChange,
        activeRecordingsAndTranscripts, 
        activeSession,
        createNotesWithSelected,
    } = useHistory();

    const [step, setStep] = useState<NewNotePage>(NewNotePage.INITIAL_SELECTION)
    const maxWidth = useMemo(() => {
        return step === NewNotePage.SELECT_RECORDINGS ? "sm:max-w-3xl" : "sm:max-w-lg"
    }, [step])

    const [template, setTemplate] = useState<TemplateObject>();    

    const [selectedRecordings, setSelectedRecordings] = useState<string[]>([]);
    
    useEffect(() => {
        if (showAddNoteModal && activeRecordingsAndTranscripts) {
            setSelectedRecordings(activeRecordingsAndTranscripts.filter(pair => pair.transcript_status === "Completed").map(pair => pair.recording_id));
        }
    }, [showAddNoteModal, activeRecordingsAndTranscripts]);

    async function handleGenerateUsingExisting() {
        if (await createNotesWithSelected(selectedRecordings, template)) {
            onClose();
        }
    }

    function onClose() {
        handleShowAddNotesModalChange(false)
        setTemplate(undefined)
        setStep(NewNotePage.INITIAL_SELECTION)
        setSelectedRecordings([]);
    }

    const subTitle = useMemo(() => {
        if (step === NewNotePage.SELECT_RECORDINGS) return "Select existing recordings to generate a new note"
        if (step === NewNotePage.UPLOAD_RECORDING) return "Upload a new recording to generate a new note"
        return undefined
    }, [step])
    
    return (
        <Modal
            isOpen={showAddNoteModal}
            onClose={onClose}
            maxWidth={maxWidth}
            title={`New Note for ${activeSession?.name || ''}`}
            subTitle={subTitle}
            icon={<DocumentPlusIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />}
        >
            {step === NewNotePage.INITIAL_SELECTION && <NewNoteInitialSelection
                template={template}
                setTemplate={setTemplate}
                handleUploadRecording={() => setStep(NewNotePage.UPLOAD_RECORDING)}
                handleReuseExistingRecordings={() => setStep(NewNotePage.SELECT_RECORDINGS)}
            />}
            {step === NewNotePage.UPLOAD_RECORDING && <UploadRecordingMainPage 
                onBack={() => setStep(NewNotePage.INITIAL_SELECTION)}
                template={template}
                onClose={onClose}
                mode={RecordingUploadMode.CREATE_NEW_NOTE}
            />}
            {step === NewNotePage.SELECT_RECORDINGS && <SelectInputsForNotes 
                template={template}
                selectedRecordings={selectedRecordings}
                setSelectedRecordings={setSelectedRecordings}
                primaryButtonText="Generate new note"
                handlePrimaryButtonClick={handleGenerateUsingExisting}
                onBack={() => setStep(NewNotePage.INITIAL_SELECTION)}
            />}
        </Modal>
    );
}
